import { useState, useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { FiArrowLeft, FiEye } from 'react-icons/fi';

import { questionsApi } from '@/services/questions';
import { Button, ButtonIcon } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/custom/loader';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHeaderWrapper, TableRow } from '@/components/ui/table';
import { QuestionDetailContent, MODALITY_LABEL, ETAPA_LABEL } from '@/components/custom/question/question-detail-content';

const TABLE_HEAD = [{ label: 'Código', width: 120 }, { label: 'Tipo', width: 130 }, { label: 'Etapa', width: 80 }, { label: 'Matéria', width: 160 }, { label: 'Comando' }, { label: 'Ações', width: 90 }];

function truncate(text = '', n = 110) {
  const clean = String(text)
    .replace(/<[^>]+>/g, '')
    .trim();
  return clean.length > n ? `${clean.slice(0, n - 1)}…` : clean;
}

export default function QuestionsList({ filters, onBack }) {
  const [questions, setQuestions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({});
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    setLoading(true);
    setPage(1);
    questionsApi
      .index({ filters, page: 1, perPage: 25 })
      .then(json => {
        const data = json?.data || [];
        setQuestions(data);
        setMeta(json?.meta || {});
        setHasMore(data.length >= 25);
      })
      .catch(() => {
        setQuestions([]);
      })
      .finally(() => setLoading(false));
  }, [filters]);

  const loadMore = () => {
    const next = page + 1;
    questionsApi.index({ filters, page: next, perPage: 25 }).then(json => {
      const items = json?.data || [];
      setQuestions(prev => [...prev, ...items]);
      setHasMore(items.length >= 25);
      setPage(next);
    });
  };

  const openQuestion = questions.find(q => q.id === openId);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={onBack}
        >
          <ButtonIcon>
            <FiArrowLeft />
          </ButtonIcon>
          Ajustar filtros
        </Button>
        <span className="text-xs text-neutral">
          {questions.length} de {meta.total_count ?? '?'} questões
        </span>
      </div>

      {loading ? (
        <Loader />
      ) : questions.length === 0 ? (
        <div className="rounded-lg border border-neutral-lightest bg-white p-8 text-center text-sm text-neutral">Nenhuma questão bate com os filtros.</div>
      ) : (
        <InfiniteScroll
          dataLength={questions.length}
          next={loadMore}
          hasMore={hasMore}
          loader={<Loader />}
          className="infinite-scroll__full"
        >
          <Table className="min-w-[900px]">
            <TableHeaderWrapper tableHead={TABLE_HEAD} />
            <TableBody>
              {questions.map(q => {
                const a = q.attributes || {};
                const taxs = a.taxonomies || [];
                const materia = taxs.find(t => ['Matemática', 'Língua Portuguesa', 'Lingua Portuguesa'].includes(t.name))?.name || '—';
                return (
                  <TableRow key={q.id}>
                    <TableCell className="font-mono text-xs text-neutral">{a['external-id'] || a.code || `#${q.id}`}</TableCell>
                    <TableCell>
                      <Badge
                        variant="neutral-lightest"
                        size="sm"
                      >
                        {MODALITY_LABEL[a.modality] || a.modality || '—'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {a.etapa ? (
                        <Badge
                          variant="default"
                          size="sm"
                        >
                          {ETAPA_LABEL[a.etapa] || a.etapa}
                        </Badge>
                      ) : (
                        <span className="text-xs text-neutral-light">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{materia}</TableCell>
                    <TableCell className="text-sm">{truncate(a.description, 100)}</TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="xs"
                        onClick={() => setOpenId(q.id)}
                      >
                        <ButtonIcon>
                          <FiEye />
                        </ButtonIcon>
                        Ver
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </InfiniteScroll>
      )}

      {/* Preview sheet */}
      <Sheet
        open={!!openId}
        onOpenChange={open => !open && setOpenId(null)}
      >
        <SheetContent
          side="right"
          className="flex w-full max-w-2xl flex-col gap-0 overflow-hidden p-0"
        >
          {openQuestion && (
            <>
              <QuestionDetailContent question={openQuestion} />

              {/* Footer */}
              <div className="flex-shrink-0 border-t border-neutral-lightest bg-white px-6 py-3">
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setOpenId(null)}
                    className="transition-transform duration-150 active:scale-[0.97]"
                    style={{ transitionTimingFunction: 'cubic-bezier(0.23, 1, 0.32, 1)' }}
                  >
                    Fechar
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
